import useReveal from "@/hooks/useReveal";
import { Calendar, FileText, Mic, ClipboardCheck, Users, Receipt, ChevronRight } from "lucide-react";

/* CSS-built product mockup that looks like LumeroFlow's actual UI */
const ProductMockup = () => (
  <div className="hero-mockup-shadow rounded-2xl overflow-hidden bg-white border border-[#1A1B1A]/[0.06]" data-testid="hero-mockup-image">
    {/* Window chrome */}
    <div className="flex items-center gap-2 px-4 py-2.5 bg-[#FAFAF8] border-b border-[#1A1B1A]/[0.06]">
      <div className="flex gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-[#1A1B1A]/10" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#1A1B1A]/10" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#1A1B1A]/10" />
      </div>
      <div className="flex-1 flex justify-center">
        <div className="bg-white rounded-md px-3 py-1 border border-[#1A1B1A]/[0.06] text-[10px] text-[#757675] font-medium tracking-wide">app.lumeroflow.co.uk</div>
      </div>
    </div>
    {/* App layout */}
    <div className="flex min-h-[320px] md:min-h-[380px]">
      {/* Sidebar */}
      <div className="w-[52px] md:w-[56px] bg-[#131E1E] flex flex-col items-center py-4 gap-4 flex-shrink-0">
        <div className="w-7 h-7 rounded-lg bg-[#205B5A] flex items-center justify-center">
          <span className="text-[9px] font-bold text-white tracking-tight">LF</span>
        </div>
        <div className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center mt-2">
          <Calendar className="w-3.5 h-3.5 text-white/70" />
        </div>
        <div className="w-7 h-7 rounded-md bg-white/[0.05] flex items-center justify-center">
          <Users className="w-3.5 h-3.5 text-white/40" />
        </div>
        <div className="w-7 h-7 rounded-md bg-white/[0.05] flex items-center justify-center">
          <Mic className="w-3.5 h-3.5 text-white/40" />
        </div>
        <div className="w-7 h-7 rounded-md bg-white/[0.05] flex items-center justify-center">
          <Receipt className="w-3.5 h-3.5 text-white/40" />
        </div>
      </div>
      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-[#1A1B1A]/[0.06]">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-[#1A1B1A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Today's Schedule</span>
            <span className="text-[10px] text-[#205B5A] bg-[#205B5A]/[0.08] px-2 py-0.5 rounded-full font-medium">8 appointments</span>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <div className="h-6 px-2.5 rounded-md bg-[#205B5A] flex items-center gap-1">
              <span className="text-[10px] text-white font-medium">New booking</span>
            </div>
          </div>
        </div>
        {/* Calendar + Panel */}
        <div className="flex flex-1 min-h-0">
          {/* Calendar grid */}
          <div className="flex-1 p-3 md:p-4 min-w-0">
            <div className="grid grid-cols-5 gap-1 mb-2">
              {["Mon", "Tue", "Wed", "Thu", "Fri"].map((d) => (
                <div key={d} className="text-[9px] text-[#757675] text-center font-medium">{d}</div>
              ))}
            </div>
            {/* Time slots */}
            <div className="space-y-1">
              {[
                { time: "9:00", name: "J. Peterson", type: "Initial", color: "#205B5A" },
                { time: "9:45", name: "M. Rivera", type: "Follow-up", color: "#496275" },
                { time: "10:30", name: "S. Thompson", type: "Assessment", color: "#205B5A" },
                { time: "11:15", name: "L. Chen", type: "Review", color: "#496275" },
                { time: "12:00", name: "", type: "Lunch", color: "transparent" },
                { time: "13:00", name: "A. Williams", type: "Initial", color: "#205B5A" },
                { time: "13:45", name: "R. Kumar", type: "Follow-up", color: "#496275" },
              ].map((slot, i) => (
                <div key={i} className={`flex items-center gap-2 py-1.5 px-2 rounded-md ${slot.name ? "bg-[#FAF9F6] hover:bg-[#F3F2EE]" : ""} transition-colors`}>
                  <span className="text-[10px] text-[#757675] w-8 flex-shrink-0 font-mono">{slot.time}</span>
                  {slot.name ? (
                    <>
                      <div className="w-1 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: slot.color }} />
                      <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-medium text-[#1A1B1A] block truncate">{slot.name}</span>
                        <span className="text-[9px] text-[#757675]">{slot.type}</span>
                      </div>
                    </>
                  ) : (
                    <span className="text-[10px] text-[#757675]/50 italic">{slot.type}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Right panel: Active patient */}
          <div className="hidden md:flex flex-col w-[200px] lg:w-[220px] border-l border-[#1A1B1A]/[0.06] p-4 bg-[#FAFAF8]">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-full bg-[#205B5A]/10 flex items-center justify-center">
                <span className="text-[9px] font-semibold text-[#205B5A]">JP</span>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-[#1A1B1A]">J. Peterson</p>
                <p className="text-[9px] text-[#757675]">Initial consultation</p>
              </div>
            </div>
            <div className="space-y-2 mb-3">
              <div className="bg-white rounded-lg p-2.5 border border-[#1A1B1A]/[0.05]">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <FileText className="w-3 h-3 text-[#205B5A]" />
                  <span className="text-[9px] font-semibold text-[#1A1B1A]">Patient record</span>
                </div>
                <div className="space-y-1">
                  <div className="h-1.5 bg-[#1A1B1A]/[0.06] rounded-full w-full" />
                  <div className="h-1.5 bg-[#1A1B1A]/[0.06] rounded-full w-3/4" />
                  <div className="h-1.5 bg-[#1A1B1A]/[0.06] rounded-full w-5/6" />
                </div>
              </div>
              <div className="bg-[#205B5A]/[0.04] rounded-lg p-2.5 border border-[#205B5A]/10">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <ClipboardCheck className="w-3 h-3 text-[#205B5A]" />
                  <span className="text-[9px] font-semibold text-[#205B5A]">AI note draft</span>
                </div>
                <div className="space-y-1">
                  <div className="h-1.5 bg-[#205B5A]/10 rounded-full w-full" />
                  <div className="h-1.5 bg-[#205B5A]/10 rounded-full w-2/3" />
                </div>
                <div className="mt-2 flex gap-1.5">
                  <span className="text-[8px] bg-[#205B5A] text-white px-1.5 py-0.5 rounded font-medium">Approve</span>
                  <span className="text-[8px] text-[#205B5A] border border-[#205B5A]/20 px-1.5 py-0.5 rounded font-medium">Edit</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const HeroSection = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center pt-[72px] overflow-hidden"
      style={{ background: "linear-gradient(180deg, #FAF9F6 0%, #F3F2EE 100%)" }}
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, #1A1B1A 1px, transparent 0)",
        backgroundSize: "48px 48px"
      }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full py-20 md:py-32 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Text */}
          <div className={`${isVisible ? "animate-fade-in-up" : "opacity-0"}`}>
            <p
              data-testid="hero-eyebrow"
              className="text-sm font-medium uppercase tracking-[0.2em] text-[#205B5A] mb-6"
            >
              Clinic software platform
            </p>
            <h1
              data-testid="hero-headline"
              className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl font-medium tracking-tight leading-[1.1] text-[#1A1B1A] mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              Clinic management and AI consultation notes in one platform
            </h1>
            <p
              data-testid="hero-subheadline"
              className="text-base md:text-lg text-[#4A4B4A] leading-relaxed max-w-xl mb-10"
            >
              LumeroFlow helps private clinics manage appointments, patient records, invoicing, and AI-powered consultation documentation in one seamless workflow.
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <a
                href="/app/signup"
                data-testid="hero-signup-btn"
                className="bg-[#205B5A] text-white px-8 py-4 rounded-full font-medium text-base hover:bg-[#174544] transition-all duration-300 shadow-sm hover:shadow-md inline-flex items-center gap-2"
              >
                Sign up now
                <ChevronRight className="w-4 h-4" />
              </a>
              <a
                href="https://www.lumeroflow.co.uk"
                data-testid="hero-demo-btn"
                className="border border-[#1A1B1A]/20 text-[#1A1B1A] px-8 py-4 rounded-full font-medium text-base hover:border-[#1A1B1A]/40 transition-all duration-300 inline-flex items-center"
              >
                Book a demo
              </a>
            </div>
            <a
              href="/app/login"
              data-testid="hero-login-link"
              className="text-sm text-[#757675] hover:text-[#205B5A] transition-colors duration-200"
            >
              Already have an account? Log in
            </a>
          </div>

          {/* Right: Product Mockup */}
          <div className={`relative ${isVisible ? "animate-fade-in-up delay-300" : "opacity-0"}`}>
            <ProductMockup />
          </div>
        </div>
      </div>

      {/* Trusted by indicators */}
      <div className={`max-w-7xl mx-auto px-6 md:px-12 w-full pb-16 md:pb-24 ${isVisible ? "animate-fade-in delay-600" : "opacity-0"}`}>
        <div className="flex flex-wrap items-center gap-x-10 gap-y-3">
          <span className="text-xs uppercase tracking-[0.15em] text-[#757675] font-medium">Designed for</span>
          {["Osteopaths", "Physiotherapists", "Chiropractors", "Therapists", "Private Clinics"].map((item) => (
            <span
              key={item}
              className="text-xs text-[#757675] font-medium flex items-center gap-2"
            >
              <span className="w-1 h-1 rounded-full bg-[#205B5A]" />
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
